import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, Factory, BarChart3, Star } from 'lucide-react';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { useSystemStats } from '../hooks/useSystemStats';
import { servicesAPI, visitorsAPI, teamAPI } from '../services/api';
import { phase1Team, phase2Team, phase3Team, phase4Team } from '../data/teams';
import './WelcomePage.css';
import './Homepage.css';

export function WelcomePage({ onLogin, onSignUp }) {
  const [selectedPhase, setSelectedPhase] = useState(4);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState({
    members: phase2Team,
    loading: false
  });

  // Fetch Phase 2 team members from database (with static fallback)
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await teamAPI.getAll();
        const members = response?.data?.data || response?.data || [];
        if (Array.isArray(members) && members.length > 0) {
          setTeamMembers({ members, loading: false });
        }
      } catch (error) {
        setTeamMembers({ members: phase2Team, loading: false });
      }
    };
    fetchTeamMembers();
  }, []);

  const { farmerCount, processorCount, municipalityCount, loading: statsLoading, error: statsError } = useSystemStats();

  // ── Visitor tracking state ──
  const [visitorCount, setVisitorCount]   = useState({ total_visits: 0, unique_visitors: 0 });
  const [visitorLoading, setVisitorLoading] = useState(true);
  const hasRecordedRef = useRef(false);

  // ── Helper: generate or retrieve a persistent anonymous token ──
  const getVisitorToken = useCallback(() => {
    const KEY = 'bciis_visitor_token';
    let token = localStorage.getItem(KEY);
    if (!token) {
      token = 'vt_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(KEY, token);
    }
    return token;
  }, []);

  // ── Helper: generate or retrieve a session id ──
  const getSessionId = useCallback(() => {
    const KEY = 'bciis_session_id';
    let sid = sessionStorage.getItem(KEY);
    if (!sid) {
      sid = 'sid_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(KEY, sid);
    }
    return sid;
  }, []);

  // ── Helper: detect client device type & brand ──
  const getClientDeviceInfo = useCallback(() => {
    const ua = (navigator.userAgent || '').toLowerCase();
    
    // Type
    let deviceType = 'Desktop';
    if (/ipad|tablet|(android(?!.*mobile))/i.test(ua)) {
      deviceType = 'Tablet';
    } else if (/mobile|iphone|ipod|android.*mobile|blackberry|iemobile|opera mini|webos|windows phone/i.test(ua)) {
      deviceType = 'Mobile Phone';
    } else if (/macintosh|windows|linux|cros/i.test(ua)) {
      deviceType = (window.screen.width <= 1440 && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) ? 'Laptop' : 'Desktop';
    }

    // Brand (Comprehensive Cellphone & PC Brands)
    let deviceBrand = 'Unknown';
    if (/iphone|ipad|ipod|macintosh|mac os x/i.test(ua)) deviceBrand = 'Apple';
    else if (/samsung|sm-[a-z0-9]+|galaxy|gt-[a-z0-9]+/i.test(ua)) deviceBrand = 'Samsung';
    else if (/xiaomi|redmi|poco|black\s*shark|\bm2[0-9]{3}[a-z]*\b|\b2[1-4][0-9]{2}[a-z0-9]*\b/i.test(ua)) deviceBrand = 'Xiaomi';
    else if (/vivo|\biqoo\b|\bv2[0-9]{3}\b|\bv1[0-9]{3}\b|\by[0-9]{2,3}\b|\bx[0-9]{2,3}\b/i.test(ua)) deviceBrand = 'Vivo';
    else if (/oppo|\bcph[0-9]{4}\b|\bfind\s*[xn][0-9]*\b|\breno\b|\bpg[a-z0-9]+\b/i.test(ua)) deviceBrand = 'Oppo';
    else if (/realme|\brmx[0-9]{4}\b|\bnarzo\b/i.test(ua)) deviceBrand = 'Realme';
    else if (/\btecno\b|\bcamon\b|\bpova\b|\bspark\b|\bphantom\b|\bpop\s*[0-9]+/i.test(ua)) deviceBrand = 'Tecno';
    else if (/\binfinix\b|\bzero\s*[0-9]+\b|\bhot\s*[0-9]+\b|\bnote\s*[0-9]+\b|\bsmart\s*[0-9]+\b|\bx[56][0-9]{2,3}\b/i.test(ua)) deviceBrand = 'Infinix';
    else if (/\bitel\b|\bvision\s*[0-9]+\b|\bit[0-9]{4}\b|\bw[0-9]{4}\b/i.test(ua)) deviceBrand = 'itel';
    else if (/huawei|honor|hma-|eml-|clt-|vog-|ele-|vce-|mar-|ana-|els-|nop-|jny-|ane-|pot-|dli-|nova|mate|magic/i.test(ua)) deviceBrand = 'Huawei';
    else if (/pixel|nexus/i.test(ua)) deviceBrand = 'Google';
    else if (/oneplus|\bnord\b|\bgm19\b|\bkb20\b|\bne22\b|\bcph2[45][0-9]{2}\b/i.test(ua)) deviceBrand = 'OnePlus';
    else if (/xperia|sony|\bso-[0-9a-z]+\b|\bsog[0-9]+\b/i.test(ua)) deviceBrand = 'Sony';
    else if (/motorola|\bmoto\b|\bxt[0-9]{4}\b|\brazr\b|\bedge\b/i.test(ua)) deviceBrand = 'Motorola';
    else if (/nokia|\bhmd\b|\bta-[0-9]{4}\b/i.test(ua)) deviceBrand = 'Nokia';
    else if (/nothing|\ba063\b|\ba065\b|\bcmf\b/i.test(ua)) deviceBrand = 'Nothing';
    else if (/zte|\bnubia\b|\bredmagic\b|\bblade\b|\baxon\b/i.test(ua)) deviceBrand = 'ZTE';
    else if (/cherry\s*mobile|cherry|\bflare\b|\baqua\b|\bcubix\b|\bcosmo\b/i.test(ua)) deviceBrand = 'Cherry Mobile';
    else if (/myphone|\bmyx[0-9]+\b|\bmyw[0-9]+\b|\bmyg[0-9]+\b/i.test(ua)) deviceBrand = 'MyPhone';
    else if (/starmobile/i.test(ua)) deviceBrand = 'Starmobile';
    else if (/cloudfone/i.test(ua)) deviceBrand = 'Cloudfone';
    else if (/torque/i.test(ua)) deviceBrand = 'Torque';
    else if (/asus|\brog\s*phone\b|\bzenfone\b|\basus_[a-z0-9]+\b/i.test(ua)) deviceBrand = 'Asus';
    else if (/lenovo|\blegion\s*phone\b/i.test(ua)) deviceBrand = 'Lenovo';
    else if (/meizu/i.test(ua)) deviceBrand = 'Meizu';
    else if (/tcl|\balcatel\b/i.test(ua)) deviceBrand = 'TCL';
    else if (/oukitel|\bblackview\b|\bdoogee\b|\bulefone\b|\bumidigi\b/i.test(ua)) deviceBrand = 'Oukitel';
    else if (/sharp|\baquos\b/i.test(ua)) deviceBrand = 'Sharp';
    else if (/kyocera|\bdigno\b/i.test(ua)) deviceBrand = 'Kyocera';
    else if (/htc|\bdesire\b|\bu1[12]\b/i.test(ua)) deviceBrand = 'HTC';
    else if (/\blg-[a-z0-9]+\b|\blm-[a-z0-9]+\b/i.test(ua)) deviceBrand = 'LG';
    else if (/micromax|\blava\b|\bsymphony\b|\bwalton\b/i.test(ua)) deviceBrand = 'Micromax';
    else if (/dell/i.test(ua)) deviceBrand = 'Dell';
    else if (/hp|hewlett-packard/i.test(ua)) deviceBrand = 'HP';
    else if (/acer/i.test(ua)) deviceBrand = 'Acer';
    else if (/msi/i.test(ua)) deviceBrand = 'MSI';
    else if (/surface/i.test(ua)) deviceBrand = 'Microsoft Surface';
    else if (/cros/i.test(ua)) deviceBrand = 'Chromebook';
    else if (/windows/i.test(ua)) deviceBrand = 'Windows PC';
    else if (/linux/i.test(ua)) deviceBrand = 'Linux';
    else if (/android/i.test(ua)) deviceBrand = 'Android Device';

    return { deviceType, deviceBrand };
  }, []);

  // ── Record visit once per visitor session & load count on mount ──
  useEffect(() => {
    let isMounted = true;

    const trackAndLoad = async () => {
      const sessionId = getSessionId();
      const visitorToken = getVisitorToken();
      const sessionKey = `bciis_recorded_${sessionId}`;

      // Check if this browsing session has already been recorded (prevents duplicate on refresh)
      const alreadyRecorded = sessionStorage.getItem(sessionKey);

      if (!alreadyRecorded && !hasRecordedRef.current) {
        hasRecordedRef.current = true;
        sessionStorage.setItem(sessionKey, 'true');

        const { deviceType, deviceBrand } = getClientDeviceInfo();

        // Exact local date and time of visit (YYYY-MM-DD HH:mm:ss)
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const exactVisitedAt = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

        try {
          const res = await visitorsAPI.record({
            visitor_token: visitorToken,
            device_type:   deviceType,
            device_brand:  deviceBrand,
            page_url:      window.location.href,
            referrer_url:  document.referrer || null,
            session_id:    sessionId,
            visited_at:    exactVisitedAt,
          });
          console.log('[BCIIS] Visit recorded:', res);
        } catch (recordErr) {
          console.error('[BCIIS] Visit recording error:', recordErr);
        }
      }

      // Fetch current statistics from database
      if (isMounted) setVisitorLoading(true);
      try {
        const counts = await visitorsAPI.getCount();
        if (isMounted) {
          setVisitorCount(counts || { total_visits: 0, unique_visitors: 0 });
        }
      } catch (countErr) {
        console.error('[BCIIS] Visitor count error:', countErr);
      } finally {
        if (isMounted) setVisitorLoading(false);
      }
    };

    trackAndLoad();

    return () => {
      isMounted = false;
    };
  }, [getVisitorToken, getSessionId, getClientDeviceInfo]);

  // Static logos
  const welcomeLogos = [
    { src: '/Bisu-logo.png', alt: 'BISU Logo' },
    { src: '/bohol-cacao-logo.png', alt: 'Bohol Cacao Logo' },
    { src: '/DOST-logo.png', alt: 'DOST Logo' },
  ];

  // Featured services state
  const [featuredServices, setFeaturedServices] = useState([]);

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        const featuredIds = JSON.parse(localStorage.getItem('bciis_featured_services') || '[]');
        if (featuredIds.length === 0) return;

        let allServices = [];
        // Try API first
        try {
          if (servicesAPI && servicesAPI.getAll) {
            const res = await servicesAPI.getAll();
            allServices = Array.isArray(res) ? res : (res?.data || res?.rows || []);
          }
        } catch {
          // Fallback to localStorage
          try {
            allServices = JSON.parse(localStorage.getItem('bciis_admin_offers_v1') || '[]');
          } catch { /* ignore */ }
        }

        const featured = allServices.filter(s => featuredIds.includes(s.id || s.service_id));
        setFeaturedServices(featured);
      } catch { /* ignore */ }
    };
    loadFeatured();
  }, []);

  return (
    <div className="min-h-screen py-8">

      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section with Background */}
        <div className="relative overflow-hidden bg-gradient-to-br from-green-50 via-amber-50 to-orange-50 rounded-3xl mb-16 p-8 md:p-16">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-3xl float-animation"></div>
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-green-200/30 to-teal-200/30 rounded-full blur-3xl" style={{animationDelay: '1s'}}></div>
          </div>
          
          {/* Hero Images Grid */}
          <div className="absolute inset-0 opacity-10">
            <div className="grid grid-cols-3 gap-4 h-full">
              <div className="space-y-4">
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=300&fit=crop" 
                  alt="Cacao pods" 
                  className="w-full h-32 object-cover rounded-lg hover-scale"
                />
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop" 
                  alt="Cacao farmer" 
                  className="w-full h-40 object-cover rounded-lg hover-scale slide-in-left"
                />
              </div>
              <div className="space-y-4 mt-8">
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=400&h=300&fit=crop" 
                  alt="Cacao processing" 
                  className="w-full h-36 object-cover rounded-lg hover-scale fade-in-up"
                />
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=400&h=300&fit=crop" 
                  alt="Chocolate bars" 
                  className="w-full h-32 object-cover rounded-lg hover-scale"
                />
              </div>
              <div className="space-y-4">
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop" 
                  alt="Bohol landscape" 
                  className="w-full h-40 object-cover rounded-lg hover-scale slide-in-right"
                />
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop" 
                  alt="Cacao beans" 
                  className="w-full h-28 object-cover rounded-lg hover-scale"
                />
              </div>
            </div>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 text-center">
            <div className="flex justify-center items-center mb-6 gap-6">
              {welcomeLogos.map((logo) => (
                <img
                  key={logo.alt}
                  src={logo.src}
                  alt={logo.alt}
                  className="rounded-full border-2 border-amber-200 object-cover shadow-lg drop-shadow-lg"
                  style={{ width: 80, height: 80 }}
                />
              ))}
            </div>
            <h1 className="mb-6 fade-in-up text-4xl md:text-6xl font-bold">
              <span className="gradient-text typing-animation">
                Bohol Cacao Industry Information System
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-8 fade-in-up leading-relaxed" style={{animationDelay: '0.2s'}}>
              Manage and analyze cacao farmers and processors data across Bohol Province.
            </p>
            
            {/* Real-time Animated Statistics - 4 cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-3xl mx-auto">
              {statsError ? (
                <div className="col-span-3 text-center py-4">
                  <p className="text-gray-500 text-sm">{statsError}</p>
                </div>
              ) : (
                <>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 fade-in-up" style={{animationDelay: '0.4s'}}>
                    <div className={`text-2xl font-bold text-green-600 ${statsLoading ? 'animate-pulse' : ''}`}>
                      {statsLoading ? '...' : `${farmerCount.toLocaleString()}+`}
                    </div>
                    <div className="text-sm text-gray-600">Farmers</div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 fade-in-up" style={{animationDelay: '0.6s'}}>
                    <div className={`text-2xl font-bold text-amber-600 ${statsLoading ? 'animate-pulse' : ''}`}>
                      {statsLoading ? '...' : `${processorCount.toLocaleString()}+`}
                    </div>
                    <div className="text-sm text-gray-600">Processors</div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 fade-in-up" style={{animationDelay: '0.8s'}}>
                    <div className={`text-2xl font-bold text-blue-600 ${statsLoading ? 'animate-pulse' : ''}`}>
                      {statsLoading ? '...' : `${municipalityCount.toLocaleString()}+`}
                    </div>
                    <div className="text-sm text-gray-600">Municipalities</div>
                  </div>
                  {/* ── Visitors card (4th) ── */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 fade-in-up" style={{animationDelay: '1s'}}>
                    <div className={`text-2xl font-bold text-purple-600 ${visitorLoading ? 'animate-pulse' : ''}`}>
                      {visitorLoading ? '...' : `${(visitorCount.total_visits || 0).toLocaleString()}+`}
                    </div>
                    <div className="text-sm text-gray-600">Visitors</div>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 fade-in-up" style={{animationDelay: '1s'}}>
              <Button 
                onClick={onLogin}
                variant="outline"
                className="border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white px-8 py-3 hover-scale bg-white/80 backdrop-blur-sm"
              >
                Login
              </Button>
              <Button 
                onClick={onSignUp}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 hover-scale shadow-lg"
              >
                Create New Account
              </Button>
            </div>
          </div>
        </div>

        {/* System Overview with Animated Cards */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-4 text-amber-900 fade-in-up">System Features</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto fade-in-up" style={{animationDelay: '0.2s'}}>
            Tools and features designed to strengthen Bohol's cacao industry
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-amber-200 hover:shadow-2xl transition-all duration-300 hover-scale slide-in-left relative overflow-hidden group">
              {/* Card Background Image */}
              <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop" 
                  alt="Cacao farmer background" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <CardHeader className="relative z-10">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-green-100 rounded-full pulse-animation">
                    <Users className="h-8 w-8 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-amber-900">Farmers Directory</CardTitle>
                    <CardDescription>Complete farmer profiles and data</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-gray-600 mb-4">
                  Registered cacao farmer profiles including farming practices, production capacity, and contact information.
                </p>
                <div className="flex space-x-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-700">Farm Management</Badge>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">Analytics</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 hover:shadow-2xl transition-all duration-300 hover-scale fade-in-up relative overflow-hidden group" style={{animationDelay: '0.2s'}}>
              {/* Card Background Image */}
              <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=400&h=300&fit=crop" 
                  alt="Cacao processing background" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <CardHeader className="relative z-10">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-amber-100 rounded-full pulse-animation" style={{animationDelay: '0.5s'}}>
                    <Factory className="h-8 w-8 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-amber-900">Processors Hub</CardTitle>
                    <CardDescription>Processing business information</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-gray-600 mb-4">
                  Directory of processing businesses, cooperatives, and enterprises with operational and market data.
                </p>
                <div className="flex space-x-2">
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700">Business</Badge>
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700">Network</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 hover:shadow-2xl transition-all duration-300 hover-scale slide-in-right relative overflow-hidden group" style={{animationDelay: '0.4s'}}>
              {/* Card Background Image */}
              <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
                <ImageWithFallback 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop" 
                  alt="Analytics background" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <CardHeader className="relative z-10">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-blue-100 rounded-full pulse-animation" style={{animationDelay: '1s'}}>
                    <BarChart3 className="h-8 w-8 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-amber-900">Analytics Dashboard</CardTitle>
                    <CardDescription>Data insights and visualization</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-gray-600 mb-4">
                  Analytics with charts, maps, and insights for the cacao industry landscape across Bohol.
                </p>
                <div className="flex space-x-2">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">Data Insights</Badge>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">Reports</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* About Summary */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-4 text-amber-900">About BCIIS</h2>
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              The Bohol Cacao Industry Information System (BCIIS) is a digital platform for managing and analyzing 
              Bohol's cacao industry through real-time data analytics, geospatial mapping, and comprehensive 
              industry information. BCIIS serves farmers, processors, researchers, government agencies, and 
              development partners with actionable insights.
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="bg-amber-50 rounded-xl p-5">
                <h3 className="font-semibold text-amber-900 mb-2">Our Mission</h3>
                <p className="text-gray-600 text-sm">To create a data-driven platform that manages and analyzes the cacao value chain in Bohol, providing real-time analytics for sustainable growth.</p>
              </div>
              <div className="bg-green-50 rounded-xl p-5">
                <h3 className="font-semibold text-green-800 mb-2">Our Vision</h3>
                <p className="text-gray-600 text-sm">To establish Bohol as a model for sustainable, technology-driven cacao production where stakeholders prosper through informed decisions.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Services */}
        {featuredServices.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-4 text-amber-900">Featured Services</h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">Services offered through the platform</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {featuredServices.map((service) => (
                <Card key={service.id || service.service_id} className="border-amber-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-amber-100 rounded-full shrink-0">
                        <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{service.title || service.service_name}</h3>
                        <p className="text-sm text-gray-600 mb-2">{service.description || service.service_description}</p>
                        {(service.contact || service.service_contact) && (
                          <p className="text-xs text-amber-700">Contact: {service.contact || service.service_contact}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Meet the Team */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-2 text-amber-900">Meet the Team</h2>
          <p className="text-center text-gray-600 mb-6 max-w-2xl mx-auto">The people behind BCIIS</p>

          {/* Phase Selector Dropdown */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-800 text-white rounded-lg shadow-lg hover:from-amber-700 hover:to-amber-900 transition-all duration-300"
                aria-expanded={isDropdownOpen}
                aria-haspopup="listbox"
                aria-label={`Select phase. Current: Phase ${selectedPhase}`}
              >
                <span className="font-semibold">Phase {selectedPhase}</span>
                <svg 
                  className={`w-5 h-5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-xl border-2 border-amber-200 overflow-hidden z-20" role="listbox" aria-label="Select phase">
                  {[1, 2, 3, 4].map((phaseNum) => (
                    <button
                      key={phaseNum}
                      role="option"
                      aria-selected={selectedPhase === phaseNum}
                      onClick={() => {
                        setSelectedPhase(phaseNum);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full px-6 py-3 text-left hover:bg-amber-50 transition-colors ${selectedPhase === phaseNum ? 'bg-amber-100 font-semibold' : ''}`}
                    >
                      Phase {phaseNum}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">
            {(
              selectedPhase === 4 ? phase4Team :
              selectedPhase === 3 ? phase3Team :
              selectedPhase === 2 ? (teamMembers.members?.length > 0 ? teamMembers.members : phase2Team) :
              phase1Team
            ).map((member, index) => (
              <div key={member.Team_Member_ID || index} className="flex flex-col items-center group w-24">
                <div className="w-16 h-16 mb-2 rounded-full border-2 border-amber-300 shadow-md overflow-hidden transition-transform duration-300 group-hover:scale-110 bg-amber-50">
                  <ImageWithFallback
                    src={member.Photo_URL}
                    alt={member.Full_Name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xs font-semibold text-gray-800 text-center leading-tight">{member.Full_Name}</h3>
                <p className="text-[11px] text-amber-700 text-center">{member.Role}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information with Enhanced Design */}
        <div className="relative overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-8 rounded-3xl">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-green-200/30 to-emerald-200/30 rounded-full blur-3xl float-animation"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-teal-200/30 to-cyan-200/30 rounded-full blur-3xl" style={{animationDelay: '1.5s'}}></div>
          </div>
          
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-center mb-6 text-green-800 fade-in-up">Get Involved</h2>
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-lg text-gray-700 mb-8 fade-in-up" style={{animationDelay: '0.2s'}}>
                The Bohol Cacao Industry Information System is a collaborative effort. Whether you're a farmer, processor, 
                researcher, or stakeholder in the cacao industry, we welcome your participation and 
                contribution to this platform.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="bg-white/80 backdrop-blur-sm border-green-200 hover:shadow-lg transition-all hover-scale slide-in-left">
                  <CardContent className="pt-6 text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 pulse-animation">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <h4 className="text-green-800 font-semibold mb-2">For Farmers</h4>
                    <p className="text-gray-600 text-sm">Register your farm data for better management and insights</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/80 backdrop-blur-sm border-green-200 hover:shadow-lg transition-all hover-scale fade-in-up" style={{animationDelay: '0.4s'}}>
                  <CardContent className="pt-6 text-center">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 pulse-animation" style={{animationDelay: '0.5s'}}>
                      <Factory className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="text-green-800 font-semibold mb-2">For Processors</h4>
                    <p className="text-gray-600 text-sm">List your processing facility data for comprehensive tracking</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/80 backdrop-blur-sm border-green-200 hover:shadow-lg transition-all hover-scale slide-in-right" style={{animationDelay: '0.6s'}}>
                  <CardContent className="pt-6 text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 pulse-animation" style={{animationDelay: '1s'}}>
                      <BarChart3 className="w-6 h-6 text-blue-600" />
                    </div>
                    <h4 className="text-green-800 font-semibold mb-2">For Partners</h4>
                    <p className="text-gray-600 text-sm">Collaborate with us to enhance the platform</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Call to Action */}
              <div className="mt-8 fade-in-up" style={{animationDelay: '0.8s'}}>
                <p className="text-green-700 font-semibold mb-4">Ready to join the Bohol Cacao Community?</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button 
                    onClick={onSignUp}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-3 hover-scale shadow-lg"
                  >
                    Join Our Platform
                  </Button>
                  <Button 
                    onClick={onLogin}
                    variant="outline"
                    className="border-green-600 text-green-700 hover:bg-green-600 hover:text-white px-8 py-3 hover-scale bg-white/80 backdrop-blur-sm"
                  >
                    Login Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}